import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { leadApi } from '@/services/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Phone, Mail, Calendar, MessageSquare, ChevronLeft, ChevronRight, Send } from 'lucide-react';
import { format } from 'date-fns';

export function MyLeads() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [noteInputs, setNoteInputs] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['my-leads', user?.id, page, statusFilter],
    queryFn: () => leadApi.list(page, 20, statusFilter === 'all' ? undefined : statusFilter),
    enabled: !!user,
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => leadApi.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-leads'] });
      toast({ title: 'Lead status updated' });
    },
    onError: (err: any) => toast({ variant: 'destructive', title: 'Failed to update', description: err.message }),
  });

  const addNoteMutation = useMutation({
    mutationFn: ({ lead_id, note }: { lead_id: string; note: string }) => leadApi.addNote(lead_id, note),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['my-leads'] });
      setNoteInputs(prev => ({ ...prev, [vars.lead_id]: '' }));
      toast({ title: 'Note added' });
    },
    onError: (err: any) => toast({ variant: 'destructive', title: 'Failed to add note', description: err.message }),
  });

  const leads = data?.leads || [];
  const totalPages = data?.totalPages || 1;

  const statusColors: Record<string, string> = {
    new: 'bg-blue-100 text-blue-800',
    contacted: 'bg-amber-100 text-amber-800',
    converted: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-800',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-bold">Leads & Inquiries</h2>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[140px] h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="converted">Converted</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>
      ) : leads.length > 0 ? (
        <>
          <div className="space-y-3">
            {leads.map((lead: any) => {
              const propTitle = typeof lead.properties?.title === 'object'
                ? (lead.properties.title.en || '')
                : lead.properties?.title || '';
              const notes = lead.lead_notes || [];
              return (
                <Card key={lead.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <MessageSquare className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold">{lead.name}</span>
                          <Badge className={`text-[10px] ${statusColors[lead.status] || ''}`}>{lead.status}</Badge>
                          <Select
                            value={lead.status}
                            onValueChange={v => updateStatusMutation.mutate({ id: lead.id, status: v })}
                          >
                            <SelectTrigger className="h-6 w-[100px] text-[10px] border-dashed"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="contacted">Contacted</SelectItem>
                              <SelectItem value="converted">Converted</SelectItem>
                              <SelectItem value="closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{lead.phone}</span>
                          {lead.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{lead.email}</span>}
                        </div>
                        {lead.message && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{lead.message}</p>}
                        {propTitle && <p className="text-xs text-muted-foreground mt-1">For: {propTitle}</p>}
                        <p className="text-xs text-muted-foreground mt-1">
                          <Calendar className="h-3 w-3 inline mr-1" />{format(new Date(lead.created_at), 'MMM d, yyyy HH:mm')}
                        </p>

                        {/* Notes */}
                        {notes.length > 0 && (
                          <div className="mt-3 border-t pt-2 space-y-1">
                            <div className="text-[10px] font-medium text-muted-foreground uppercase">Notes</div>
                            {notes.map((n: any) => (
                              <div key={n.id} className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                                {n.note}
                                <span className="text-[10px] ml-2 opacity-60">{format(new Date(n.created_at), 'MMM d, HH:mm')}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Add note */}
                        <div className="flex gap-1.5 mt-2">
                          <Input
                            placeholder="Add a note..."
                            className="h-7 text-xs flex-1"
                            value={noteInputs[lead.id] || ''}
                            onChange={e => setNoteInputs(prev => ({ ...prev, [lead.id]: e.target.value }))}
                            onKeyDown={e => {
                              if (e.key === 'Enter' && noteInputs[lead.id]?.trim()) {
                                addNoteMutation.mutate({ lead_id: lead.id, note: noteInputs[lead.id] });
                              }
                            }}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            disabled={!noteInputs[lead.id]?.trim()}
                            onClick={() => addNoteMutation.mutate({ lead_id: lead.id, note: noteInputs[lead.id] })}
                          >
                            <Send className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-20 text-muted-foreground">No leads yet</div>
      )}
    </div>
  );
}
